import { Card } from "@/components/ui/card";
import { MapPin, Phone, Globe, Clock, Star, Mail, MessageSquare } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

interface Location {
  id: string;
  name: string;
  address: string;
  formatted_address: string;
  location_city?: string;
  location_short_address?: string;
  primary_phone?: string;
  website_uri?: string;
  latitude?: number;
  longitude?: number;
  regular_hours?: any;
  auto_detected_services?: string[];
  photos?: any[];
  average_rating?: number;
  reviews_count?: number;
  primary_category?: string;
  additional_categories?: any[];
  description?: string;
  distance?: number;
  media_items?: any;
}

interface StoreLocatorDetailsPanelProps {
  location: Location | null;
  theme: any;
  workspace: any;
  formatAddress: (location: Location) => string;
  formatPhoneNumber: (phone: string) => string;
  children?: React.ReactNode;
  ssrReviewsData?: any[] | undefined; // SSR pre-fetched reviews
}

interface Review {
  id: string;
  reviewer: {
    displayName: string;
    profilePhotoUrl?: string;
  };
  star_rating: string;
  comment?: string;
  create_time: string;
  rating?: number;
}

const StoreLocatorDetailsPanel = ({
  location,
  theme,
  workspace,
  formatAddress,
  formatPhoneNumber,
  children,
  ssrReviewsData,
}: StoreLocatorDetailsPanelProps) => {
  // Initialize reviews with SSR data if available
  const [reviews, setReviews] = useState<Review[]>(ssrReviewsData || []);
  const [loadingReviews, setLoadingReviews] = useState(!ssrReviewsData);
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 5;

  useEffect(() => {
    // If SSR reviews exist, use them and skip fetching
    if (ssrReviewsData && ssrReviewsData.length > 0) {
      setReviews(ssrReviewsData);
      setLoadingReviews(false);
      return;
    }

    // Otherwise, fetch reviews from database
    if (location?.id) {
      fetchReviews();
    }
  }, [location?.id, ssrReviewsData]);

  const fetchReviews = async () => {
    try {
      setLoadingReviews(true);
      const { data, error } = await supabase
        .from("gmb_reviews")
        .select("*")
        .eq("location_id", location?.id)
        .order("create_time", { ascending: false })
        .limit(10);

      if (error) {
        console.error("Error fetching reviews:", error);
        return;
      }

      setReviews(data || []);
    } catch (err) {
      console.error("Error fetching reviews:", err);
    } finally {
      setLoadingReviews(false);
    }
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />);
    }
    if (hasHalfStar && fullStars < 5) {
      stars.push(<Star key={fullStars} className="w-5 h-5 fill-yellow-400/50 text-yellow-400" />);
    }
    for (let i = stars.length; i < 5; i++) {
      stars.push(<Star key={i} className="w-5 h-5 text-gray-300" />);
    }
    return stars;
  };

  const getNumericRating = (starRating: string): number => {
    const ratingMap: { [key: string]: number } = {
      "ONE": 1,
      "TWO": 2,
      "THREE": 3,
      "FOUR": 4,
      "FIVE": 5
    };
    return ratingMap[starRating] || 0;
  };

  const getInitials = (name: string) => {
    if (!name || typeof name !== 'string') {
      return '??';
    }
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const numericReviews = reviews
    .map(review => ({
      ...review,
      rating: getNumericRating(review.star_rating)
    }))
    .filter(review => review.rating >= 4);

  // Pagination logic
  const totalPages = Math.ceil(numericReviews.length / reviewsPerPage);
  const startIndex = (currentPage - 1) * reviewsPerPage;
  const endIndex = startIndex + reviewsPerPage;
  const currentReviews = numericReviews.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Create Google Maps embed URL
  const createMapEmbedUrl = () => {
    if (!location) return null;

    // If we have a maps_url with CID, use it for embedding
    if (location.maps_url) {
      const cidMatch = location.maps_url.match(/cid=(\d+)/);
      if (cidMatch && cidMatch[1]) {
        const cid = cidMatch[1];
        return `https://maps.google.com/maps?cid=${cid}&output=embed`;
      }
    }

    // Fallback to coordinates-based embedding if available
    if (location.latitude && location.longitude) {
      const query = encodeURIComponent(`${location.name}, ${location.address || location.formatted_address}`);

      // Try with API key first if available
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      if (apiKey) {
        return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${query}&center=${location.latitude},${location.longitude}&zoom=15`;
      } else {
        // Basic embed without API key
        return `https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=15&output=embed`;
      }
    }

    return null;
  };

  const mapEmbedUrl = createMapEmbedUrl();

  if (!location) {
    return (
        <Card className="h-full overflow-hidden flex flex-col min-h-[300px]">
          <div className="flex items-center justify-center h-full text-gray-400 p-4">
            <div className="text-center">
              <MapPin className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-3 md:mb-4" />
              <p className="text-sm md:text-base">Select a location from the list to view details</p>
            </div>
          </div>
        </Card>
    );
  }

  return (
      <Card className="h-full overflow-hidden flex flex-col">
        {/* Cover Photo Banner */}
        {(() => {
          try {
            let mediaItems;
            if (typeof location.media_items === 'string') {
              mediaItems = JSON.parse(location.media_items);
            } else {
              mediaItems = location.media_items;
            }

            const coverPhotoUrl = mediaItems?.coverPhoto?.googleUrl || mediaItems?.coverPhoto?.sourceUrl || mediaItems?.coverPhoto?.thumbnailUrl;

            if (!coverPhotoUrl) {
              return null;
            }

            return (
              <div className="h-32 md:h-48 w-full overflow-hidden">
                <img
                  src={coverPhotoUrl}
                  alt={`${location.name} cover`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const imgElement = e.target as HTMLImageElement;
                    const container = imgElement.parentElement;
                    if (container) {
                      container.style.display = 'none';
                    }
                  }}
                />
              </div>
            );
          } catch (error) {
            return null;
          }
        })()}

        <div className="flex-1 p-4 md:p-6 overflow-y-auto">
          <div className="space-y-4 md:space-y-6">
            {/* Name and Logo */}
            <div className="flex items-start justify-between gap-3 md:gap-4">
              <div className="min-w-0 flex-1">
                <h2 className="text-xl md:text-2xl font-bold">{location.name}</h2>
                {/* Rating and Reviews */}
                {location.average_rating && (
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <div className="flex items-center">
                      {renderStars(location.average_rating)}
                    </div>
                    <span className="text-base md:text-lg font-semibold text-gray-700">{location.average_rating.toFixed(1)}</span>
                    {location.reviews_count && location.reviews_count > 0 && (
                      <span className="text-sm md:text-base text-gray-500">({location.reviews_count} reviews)</span>
                    )}
                  </div>
                )}
              </div>
              {/* Logo */}
              {(() => {
                try {
                  let mediaItems;
                  if (typeof location.media_items === 'string') {
                    mediaItems = JSON.parse(location.media_items);
                  } else {
                    mediaItems = location.media_items;
                  }

                  const originalThumbnailUrl = mediaItems?.logo?.thumbnailUrl;
                  const originalGoogleUrl = mediaItems?.logo?.googleUrl;
                  const logoUrl = originalGoogleUrl || originalThumbnailUrl;

                  if (!logoUrl) {
                    return null;
                  }

                  return (
                    <img
                      src={logoUrl}
                      alt={`${location.name} logo`}
                      className="h-12 w-12 md:h-16 md:w-16 rounded-lg object-cover shadow-sm flex-shrink-0"
                      onError={(e) => {
                        const imgElement = e.target as HTMLImageElement;
                        const fallbackUrl = logoUrl === originalGoogleUrl ? originalThumbnailUrl : originalGoogleUrl;

                        if (fallbackUrl && imgElement.src !== fallbackUrl) {
                          imgElement.src = fallbackUrl;
                        } else {
                          imgElement.style.display = 'none';
                        }
                      }}
                    />
                  );
                } catch (error) {
                  return null;
                }
              })()}
            </div>

            {/* Address Section with Map */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
                  <h3 className="font-semibold text-base md:text-lg">Address</h3>
                </div>
                <p className="text-sm md:text-base text-gray-600">{formatAddress(location)}</p>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(formatAddress(location))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-4 py-2 text-center text-white rounded-lg font-medium hover:opacity-90 text-sm md:text-base"
                  style={{ backgroundColor: theme?.primaryColor }}
                >
                  Get Directions
                </a>
              </div>
              {mapEmbedUrl ? (
                <div className="h-48 md:h-48 rounded-lg overflow-hidden shadow-lg">
                  <iframe
                    src={mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Location Map"
                  />
                </div>
              ) : (
                <div className="h-48 md:h-48 rounded-lg bg-gray-200 flex items-center justify-center">
                  <p className="text-gray-500 text-xs md:text-sm">Map not available</p>
                </div>
              )}
            </div>

            {/* Contact Us Section */}
            {(location.primary_phone || location.website_uri) && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Phone className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
                  <h3 className="font-semibold text-base md:text-lg">Contact Us</h3>
                </div>
                <div className="space-y-2 md:space-y-3">
                  {location.primary_phone && (
                    <div>
                      <a
                        href={`tel:${location.primary_phone}`}
                        className="text-sm md:text-base text-gray-600 hover:underline break-all"
                      >
                        {formatPhoneNumber(location.primary_phone)}
                      </a>
                    </div>
                  )}
                  {location.website_uri && (
                    <div>
                      <a
                        href={location.website_uri}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm md:text-base text-gray-600 hover:underline break-all"
                      >
                        {location.website_uri}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Business Hours Section */}
            {location.regular_hours && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
                  <h3 className="font-semibold text-base md:text-lg">Business Hours</h3>
                </div>
                <div className="text-sm md:text-base text-gray-600 space-y-1">
                  <p>Monday-Friday: 9:00 AM - 6:00 PM</p>
                  <p>Saturday-Sunday: 10:00 AM - 4:00 PM</p>
                </div>
              </div>
            )}

            {/* About Section */}
            {location.description && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
                  <h3 className="font-semibold text-base md:text-lg">About</h3>
                </div>
                <p className="text-sm md:text-base text-gray-600">{location.description}</p>
              </div>
            )}

            {/* Services Section */}
            {location.auto_detected_services && location.auto_detected_services.length > 0 && (
              <div>
                <h3 className="font-semibold text-base md:text-lg mb-3">Services</h3>
                <div className="flex flex-wrap gap-2">
                  {location.auto_detected_services.map((service: string) => (
                    <span
                      key={service}
                      className="px-2 py-1 md:px-3 md:py-1 bg-gray-100 text-gray-700 rounded-full text-xs md:text-sm"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Reviews Section */}
            {numericReviews.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Star className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
                  <h3 className="font-semibold text-base md:text-lg">Customer Reviews</h3>
                </div>
                <div className="space-y-3 md:space-y-4">
                  {currentReviews.map((review) => (
                    <div key={review.id} className="border-b pb-3 md:pb-4 last:border-b-0">
                      <div className="flex items-start gap-2 md:gap-3">
                        <div
                          className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-semibold text-white text-xs md:text-sm flex-shrink-0"
                          style={{ backgroundColor: theme?.primaryColor }}
                        >
                          {review.reviewer?.profilePhotoUrl ? (
                            <img
                              src={review.reviewer.profilePhotoUrl}
                              alt={review.reviewer?.displayName || 'Reviewer'}
                              className="w-full h-full rounded-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                if (e.currentTarget.parentElement) {
                                  e.currentTarget.parentElement.textContent = getInitials(review.reviewer?.displayName || '');
                                }
                              }}
                            />
                          ) : (
                            getInitials(review.reviewer?.displayName || '')
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                            <h4 className="font-medium text-xs md:text-sm truncate">{review.reviewer?.displayName || 'Anonymous'}</h4>
                            <span className="text-xs text-gray-500 flex-shrink-0">
                              {format(new Date(review.create_time), "dd MMM yy")}
                            </span>
                          </div>
                          <div className="flex mb-2">
                            {Array.from({ length: 5 }, (_, i) => (
                              <Star
                                key={i}
                                className={`h-3 w-3 md:h-4 md:w-4 ${
                                  i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                                }`}
                              />
                            ))}
                          </div>
                          {review.comment && (
                            <p className="text-xs md:text-sm text-gray-600">{review.comment}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-1 md:gap-2 mt-4 flex-wrap">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-2 md:px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 text-xs md:text-sm"
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-2 md:px-3 py-1 rounded border text-xs md:text-sm ${
                          currentPage === page
                            ? 'text-white'
                            : 'hover:bg-gray-100'
                        }`}
                        style={currentPage === page ? { backgroundColor: theme?.primaryColor } : {}}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-2 md:px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 text-xs md:text-sm"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Posts Section - passed as children */}
            {children}
          </div>
        </div>
      </Card>
  );
};

export default StoreLocatorDetailsPanel;
